import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { GET_VOTING_ARTICLE, CREATE_VOTE, UPDATE_VOTE } from '../graphql/voting';
import CommentSection from './CommentSection';
import { formatDate } from '../utils/dateUtils';
import AddToLibraryModal from './AddToLibraryModal';
import VotingEditModal from './VotingEditModal';

const VotingDetailModal = ({ isOpen, onClose, votingId, viewMode = 'all', currentUserId = null }) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const [selectedOption, setSelectedOption] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [isAddToLibraryModalOpen, setIsAddToLibraryModalOpen] = useState(false);
  const [showSecretInfo, setShowSecretInfo] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // 鎖定背景滾動
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const { loading, error, data, refetch } = useQuery(GET_VOTING_ARTICLE, {
    variables: { id: votingId },
    fetchPolicy: 'cache-and-network',
    skip: !isOpen || !votingId
  });

  const [createVote] = useMutation(CREATE_VOTE, {
    onCompleted: (data) => {
      if (data.createVote.success) {
        setIsVoting(false);
        refetch();
      }
    },
    onError: (error) => {
      console.error('投票失敗:', error);
      setIsVoting(false);
    }
  });

  const [updateVote] = useMutation(UPDATE_VOTE, {
    onCompleted: (data) => {
      if (data.updateVote.success) {
        setIsVoting(false);
        refetch();
      }
    },
    onError: (error) => {
      console.error('更新投票失敗:', error);
      setIsVoting(false);
    }
  });

  const handleVote = async (optionIndex) => {
    if (isVoting) return;
    const currentArticle = data?.votingArticle?.article;
    if (!currentArticle) return;
    const userVote = currentArticle.userVote;
    if (userVote && userVote.selectedOption === optionIndex) return;
    
    setIsVoting(true);
    setSelectedOption(optionIndex);
    
    try {
      if (userVote) {
        await updateVote({
          variables: {
            input: {
              articleId: currentArticle.id,
              selectedOption: optionIndex
            }
          }
        });
      } else {
        await createVote({
          variables: {
            input: {
              articleId: currentArticle.id,
              selectedOption: optionIndex
            }
          }
        });
      }
    } catch (error) {
      console.error('投票錯誤:', error);
      setIsVoting(false);
      setSelectedOption(null);
    }
  };

  const handleAddToLibraryClick = () => {
    setIsAddToLibraryModalOpen(true);
  };

  const handleAddToLibrarySuccess = () => {
    client.cache.evict({ fieldName: 'votingArticles' });
    client.cache.gc();
  };

  const calculatePercentage = (count, total) => {
    if (total === 0) return '0.00';
    return ((count / total) * 100).toFixed(2);
  };

  if (!isOpen) return null;

  const article = data?.votingArticle?.article;
  const totalVotes = article?.voteResults?.reduce((sum, result) => sum + result.count, 0) || 0;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div 
        className="bg-white rounded-none shadow-xl w-screen h-screen max-w-none max-h-none overflow-y-auto border-2 border-purple-400 flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* 標題欄 */}
          <div className="bg-white border-b border-purple-200 px-6 py-4 sticky top-0 z-20">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">投票詳情</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>
          </div>

          {/* 內容區域 */}
          <div className="py-4">
            {loading ? (
              <div className="flex justify-center items-center py-12 px-6">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12 px-6">
                <div className="text-red-600 text-base mb-4">載入投票失敗</div>
                <button 
                  onClick={() => refetch()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  重新載入
                </button>
              </div>
            ) : !article ? (
              <div className="text-center py-12 px-6">
                <div className="text-gray-600 text-base mb-4">投票不存在</div>
              </div>
            ) : (
              <div className={`${
                article.question?.secret 
                  ? 'border-2 border-yellow-400 shadow-yellow-100' 
                  : 'border border-gray-100'
              } bg-white`}>
                {/* 祕密問題提示條 */}
                {article.question?.secret && showSecretInfo && (
                  <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b border-yellow-200 px-6 py-3">
                    <div className="flex items-center justify-between text-sm text-yellow-800">
                      <div className="flex items-center">
                        <span className="mr-2">🔒</span>
                        <span className="font-medium">祕密問題：不會顯示在配對詳情和共同話題中，僅參與配對分數計算</span>
                      </div>
                      <button 
                        onClick={() => setShowSecretInfo(false)}
                        className="text-yellow-600 hover:text-yellow-800 ml-2"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                {/* 1. 標題 */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-start gap-2 mb-3">
                    <h1 className="text-xl font-bold text-gray-900 break-words flex-1">
                      {article.question?.title || '未知問題'}
                    </h1>
                    {article.question?.secret && (
                      <button
                        onClick={() => setShowSecretInfo(!showSecretInfo)}
                        className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-300 hover:bg-yellow-200 transition-colors cursor-pointer flex-shrink-0"
                      >
                        🔒 祕密
                      </button>
                    )}
                  </div>
                </div>

                {/* 2. 作者與發文時間 */}
                <div className="px-6 py-3 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-sm text-gray-600">
                    {article.isAnonymous ? (
                      <span className="flex items-center">
                        <span className="mr-1">🎭</span>
                        <span>匿名使用者</span>
                      </span>
                    ) : article.creator?.id ? (
                      <span
                        onClick={() => {
                          onClose();
                          navigate(`/user/${article.creator.id}`);
                        }}
                        className="cursor-pointer hover:text-blue-600 hover:underline transition-colors flex items-center"
                      >
                        <span className="mr-1">👤</span>
                        {article.creator.username || '匿名'}
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <span className="mr-1">👤</span>
                        {article.creator?.username || '匿名'}
                      </span>
                    )}
                    <span className="flex items-center">
                      <span className="mr-1">📅</span>
                      {formatDate(article.createdAt)}
                    </span>
                    <span className="flex items-center">
                      <span className="mr-1">💬</span>
                      {article.commentCount || 0} 則留言
                    </span>
                  </div>
                </div>

                {/* 3. 內文（描述） */}
                {article.question?.description && (
                  <div className="px-6 py-4 border-b border-gray-100">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                      {article.question.description}
                    </p>
                    {/* 標籤顯示 */}
                    {article.question?.tags && article.question.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {article.question.tags.map((tag, index) => (
                          <span
                            key={tag?.id || index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                          >
                            {tag?.name || tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* 4. 投票選項 */}
                {article.question && (
                  <div className="p-6 border-b border-gray-100">
                    <div className="space-y-3">
                      {article.question.options?.map((option, index) => {
                        const userVote = article.userVote;
                        const voteResult = article.voteResults?.find(r => r.optionIndex === index);
                        const voteCount = voteResult?.count || 0;
                        const percentage = calculatePercentage(voteCount, totalVotes);
                        const isUserVoted = userVote?.selectedOption === index;
                        const isSelected = selectedOption === index;
                        
                        return (
                          <div key={index} className="relative overflow-hidden">
                            <button
                              onClick={() => handleVote(index)}
                              disabled={isVoting || (userVote && userVote.selectedOption === index)}
                              className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 relative ${
                                isUserVoted
                                  ? 'border-yellow-400'
                                  : isSelected
                                  ? 'border-purple-400'
                                  : 'border-gray-200 hover:border-gray-300'
                              } ${isVoting ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            >
                              {/* 進度條背景 */}
                              {article.showVoteResults !== false && totalVotes > 0 && (
                                <div 
                                  className={`absolute inset-0 transition-all duration-300 ${
                                    isUserVoted ? 'bg-yellow-100' : 'bg-gray-100'
                                  }`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              )}
                              
                              <div className="relative z-10">
                                {/* 選項文本 - 自動換行 */}
                                <div className="mb-2">
                                  <span className="font-semibold text-base break-words whitespace-normal block" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                    {option.text}
                                  </span>
                                </div>
                                {/* 票數和百分比 - 顯示在最後一行 */}
                                <div className="flex items-center justify-end space-x-3 mt-2">
                                  {userVote && isUserVoted && (
                                    <span className="text-blue-700 font-medium text-sm whitespace-nowrap">
                                      ✓ 已投票
                                    </span>
                                  )}
                                  {/* 根據 showVoteResults 標誌顯示/隱藏票數分佈 */}
                                  {article.showVoteResults !== false && (
                                    <span className="text-sm text-gray-700 font-medium whitespace-nowrap">
                                      {voteCount} 票 {percentage}%
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                            
                            {isVoting && isSelected && (
                              <div className="absolute inset-0 flex items-center justify-center bg-purple-50 rounded-lg">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                  </div>
                )}

                {/* 5. 總投票數 + 加入擇友條件按鈕 */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-700 font-medium whitespace-nowrap">
                      🗳️ 總投票: {totalVotes}
                    </span>
                    <div className="flex items-center justify-end space-x-3">
                      {/* 我的投票文模式下顯示編輯按鈕，否則顯示加入擇友標準按鈕 */}
                      {viewMode === 'myPosts' && currentUserId && article.creatorId === currentUserId ? (
                        <button 
                          onClick={() => setIsEditModalOpen(true)}
                          className="px-4 py-2 rounded-lg bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-all font-medium text-sm whitespace-nowrap"
                        >
                          編輯
                        </button>
                      ) : (
                        <button 
                          onClick={handleAddToLibraryClick}
                          className="px-4 py-2 rounded-lg bg-gradient-to-r from-pink-400 to-purple-500 text-white hover:from-pink-500 hover:to-purple-600 transition-all font-medium text-sm shadow-md hover:shadow-lg whitespace-nowrap"
                        >
                          {data?.votingArticle?.article?.isInQuestionSet ? '修改擇友條件' : '加入擇友標準'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* 6. 評論區 */}
                {article.allowComments !== false && (
                  <div className="p-6 border-b border-gray-100">
                    <CommentSection articleId={article.id} />
                  </div>
                )}
                
                {article.allowComments === false && (
                  <div className="p-6 border-b border-gray-100 bg-gray-50 rounded-b-lg text-center">
                    <p className="text-base text-gray-600 font-medium">此投票不允許留言</p>
                  </div>
                )}

                {/* 7. 🆔 投票ID（最底部） */}
                <div className="px-6 py-3 bg-gray-50">
                  <div className="text-sm text-gray-500">
                    <span>🆔 {article.id}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 加入題庫彈窗 */}
      {article && (
        <AddToLibraryModal
          isOpen={isAddToLibraryModalOpen}
          onClose={() => setIsAddToLibraryModalOpen(false)}
          onSuccess={handleAddToLibrarySuccess}
          question={article.question}
        />
      )}

      {/* 編輯彈窗 */}
      {article && (
        <VotingEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          article={article}
          onSuccess={() => {
            // 刷新數據
            refetch();
            client.cache.evict({ fieldName: 'votingArticles' });
            client.cache.gc();
          }}
        />
      )}
    </>
  );
};

export default VotingDetailModal;

